import { Button } from "~/components/ui/button";
import headerLogoImg from "~/assets/header-logo.svg";
import headerTextImg from "~/assets/header-text.svg";
import Announcement from "~/components/common/Announcement";
import { useUserStore } from "~/lib/stores/user";
import { Search } from "lucide-react";
import { BlurryBackground } from "~/components/common/BlurryBackground";
import { IoPersonOutline } from "react-icons/io5";
import Login from "~/components/common/Login";
import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import GetApp from "./GetApp";
import { isGuestUser } from "~/lib/user";
import SearchInput from "~/pages/deal/search/input";
import { cn } from "~/lib/utils";
import CategoriesDropdown from "~/components/common/CategoriesDropdown";
import { useGoogleCategoriesStore } from "~/lib/stores/google-categories";

export interface HeaderProps {
  showAnnouncement?: boolean;
  showCategoriesMenu?: boolean;
  showBlidzAppMenu?: boolean;
  showSearchBar?: boolean;
  showAuthControl?: boolean;
}

export default function Header({
  showAnnouncement = false,
  showCategoriesMenu = false,
  showBlidzAppMenu = false,
  showSearchBar = true,
  showAuthControl = true,
}: HeaderProps) {
  const user = useUserStore(state => state.user);
  const isLoggedIn = !!user && !isGuestUser(user);
  const [loginOpen, setLoginOpen] = useState(false);
  const [getAppOpen, setGetAppOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [searchInputPosition, setSearchInputPosition] = useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { rootCategories, preloadAllCategories } = useGoogleCategoriesStore();

  useEffect(() => {
    if (showCategoriesMenu) {
      preloadAllCategories();
    }
  }, [showCategoriesMenu, preloadAllCategories]);

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (searchWrapperRef.current) {
        const rect = searchWrapperRef.current.getBoundingClientRect();
        setSearchInputPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    if (searchDialogOpen) {
      updatePosition();
      window.addEventListener("resize", updatePosition);
      return () => window.removeEventListener("resize", updatePosition);
    } else {
      setSearchInputPosition(null);
    }
  }, [searchDialogOpen]);

  return (
    <BlurryBackground
      position="fixed"
      className="top-0 left-0 z-50 w-screen bg-gradient-to-b from-[rgba(255,251,205,0.5)] to-[rgba(255,255,255,0.5)]"
    >
      {showAnnouncement && <Announcement />}
      <div
        id="header-content"
        className="flex h-[78px] items-center justify-between px-[24px] py-[16px] backdrop-blur-[10px] backdrop-filter"
      >
        <div id="header-left-section" className="flex h-full items-center gap-[18px]">
          <Link id="header-logo-link" to="/shop" className="flex items-center gap-[8px]">
            <div className="flex size-[30px] shrink-0 items-center justify-center overflow-hidden rounded-[7.5px] border border-[#f6f2d1] bg-[#fffce0]">
              <img id="header-logo-icon" src={headerLogoImg} alt="Blidz" className="size-[34.665px]" />
            </div>
            <img id="header-logo-text" src={headerTextImg} alt="Blidz" className="h-[22px] w-[66px]" />
          </Link>
          {(showCategoriesMenu || showBlidzAppMenu) && (
            <nav
              id="header-nav"
              className="hidden h-full items-center gap-[13px] rounded-[99px] border border-white bg-gradient-to-b from-[#f3f3f3] to-[#ffffff] px-[16px] py-[8px] md:flex"
            >
              {showCategoriesMenu && (
                <button
                  id="header-nav-categories"
                  onMouseEnter={() => {
                    setCategoriesOpen(true);
                    if (rootCategories.length === 0) {
                      preloadAllCategories();
                    }
                  }}
                  className="relative flex h-[42px] cursor-pointer items-center justify-center rounded-[6px] px-[8px] py-[8px] text-[14px] leading-[20px] font-semibold text-neutral-900"
                >
                  <span className="relative">Categories</span>
                </button>
              )}
              {showBlidzAppMenu && (
                <>
                  <button
                    id="header-button-blidz-app"
                    onClick={() => setGetAppOpen(true)}
                    className="flex h-[42px] cursor-pointer items-center justify-center gap-[4px] rounded-[6px] px-[8px] py-[8px] text-[14px] leading-[20px] font-semibold text-neutral-900"
                  >
                    Blidz App
                    <svg className="size-[12px]" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="#737373"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <GetApp open={getAppOpen} onOpenChange={setGetAppOpen} />
                </>
              )}
            </nav>
          )}
        </div>

        {showSearchBar && (
          <div id="header-search-container" className="mx-2 flex h-full w-full max-w-[500px] flex-1 flex-col md:mx-4">
            <div
              ref={searchWrapperRef}
              id="header-search-wrapper"
              className="flex h-full w-full items-center rounded-[99px] border border-white bg-gradient-to-b from-[#f3f3f3] to-[#ffffff] px-[16px] py-[8px]"
            >
              <Search id="header-search-icon" className="h-4 w-4 shrink-0 text-[#737373]" />
              <input
                id="header-search-input"
                onClick={() => setSearchDialogOpen(true)}
                type="text"
                placeholder="Search"
                className={cn(
                  "ml-[10px] w-full bg-transparent text-[14px] leading-[20px] font-medium text-neutral-500 placeholder:text-neutral-500",
                  "focus:border-none focus:shadow-none focus:outline-none",
                  searchDialogOpen && "opacity-0"
                )}
              />
            </div>
          </div>
        )}

        <div
          id="header-right-section"
          className="flex h-full shrink-0 cursor-pointer items-center justify-end gap-[8px] md:max-w-[345px]"
        >
          {showAuthControl && (
            <>
              {isLoggedIn && user ? (
                <div
                  id="header-avatar-container"
                  onClick={() => navigate("/user/profile")}
                  className="flex h-full w-[46px] items-center justify-center rounded-[99px] border border-white bg-gradient-to-b from-[#f3f3f3] to-[#ffffff] px-[16px] py-[8px]"
                >
                  <div className="flex size-[38px] items-center justify-center rounded-[99px] bg-white p-[10px]">
                    <div className="flex size-[22px] items-center justify-center overflow-hidden rounded-[31px] bg-gradient-to-r from-[#fff6b5] to-[#fdf853] p-[10px]">
                      <p className="text-center text-[12px] leading-[18px] font-semibold text-black">
                        {user.basicInfo?.name?.[0]?.toUpperCase() || "U"}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div id="header-auth-container" className="flex h-full items-center">
                  <Button
                    id="header-button-sign-in"
                    onClick={() => setLoginOpen(true)}
                    className="flex h-full items-center gap-[4px] rounded-[99px] border border-white bg-gradient-to-b from-[#f3f3f3] to-[#ffffff] px-[20px] py-[8px] text-[14px] leading-[20px] font-semibold text-neutral-900"
                  >
                    <IoPersonOutline className="size-[12px]" />
                    <span className="hidden sm:inline">Login</span>
                  </Button>
                  <Login open={loginOpen} onOpenChange={setLoginOpen} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showSearchBar && searchDialogOpen && searchInputPosition && (
        <>
          <div
            id="header-search-mask"
            className="fixed top-0 left-0 z-51 h-screen w-screen bg-black/35"
            onClick={() => setSearchDialogOpen(false)}
          />
          <div
            id="search-input-container"
            className="fixed z-52"
            style={{
              top: `${searchInputPosition.top}px`,
              left: `${searchInputPosition.left}px`,
              width: `${searchInputPosition.width}px`,
            }}
          >
            <SearchInput
              onSearch={keyword => {
                setSearchDialogOpen(false);
                navigate(`/shop/search?keyword=${encodeURIComponent(keyword)}`);
              }}
              onClose={() => setSearchDialogOpen(false)}
            />
          </div>
        </>
      )}
      {showCategoriesMenu && categoriesOpen && (
        <>
          <div
            id="categories-mask"
            className={cn(
              "fixed left-0 z-51 h-screen w-screen bg-black/35",
              showAnnouncement ? "top-[110px]" : "top-[78px]"
            )}
            onClick={() => setCategoriesOpen(false)}
          />
          <div onMouseLeave={() => setCategoriesOpen(false)}>
            <CategoriesDropdown onClick={() => setCategoriesOpen(false)} showAnnouncement={showAnnouncement} />
          </div>
        </>
      )}
    </BlurryBackground>
  );
}
